import type { TranslationKeys } from './types';

export const de: TranslationKeys = {
    // Navbar
    navbar: {
      schools: 'Schulen',
      howItWorks: 'So funktioniert\'s',
      aboutMe: 'Über mich',
      pricing: 'Preise',
      faqs: 'FAQ',
      memories: 'Erinnerungen',
      myAccount: 'Mein Konto',
    },
    // Home Page - HERO MAIN (H1, tagline, subtitle)
    home: {
      title: 'Finde deine Surffotos in Sekunden',
      tagline: 'Surf-Fotografie auf Lanzarote',
      subtitle: 'Lade ein Foto hoch und finde deine Erinnerungen sofort, ohne Hunderte von Bildern durchsuchen zu müssen',
      cta: 'Meine Fotos finden',
      stats: {
        photos: 'Fotos',
        schools: 'Schulen',
        surfers: 'Glückliche Surfer',
      },
    },
    // Schools Page - SURF SCHOOLS
    schools: {
      title: 'Surfschulen',
      subtitle: 'Wähle deine Surfschule, um deine Fotos zu finden',
      viewPhotos: 'Fotos ansehen',
      introTitle: 'Deine Schule, dein Tag, deine Erinnerungen',
      descriptionMain: 'Wähle die Surfschule, bei der du deinen Kurs gemacht hast, und finde deine Session nach Datum sortiert.',
      descriptionSecondary: 'Tägliche Surf-Fotosessions in Famara und La Santa.',
      loading: 'Schulen werden geladen...',
      apiUnavailable:
        'Der Server konnte nicht erreicht werden (Zeitüberschreitung oder Fehler). Standard-Schulliste wird angezeigt — versuche es in ein paar Minuten erneut.',
      retry: 'Erneut versuchen',
      otherSchools: 'WEITERE SCHULEN',
      valueHighQuality: 'Fotos in hoher Qualität',
      valueInstantDownload: 'Sofortiger Download',
      valueSecurePayment: 'Sichere Zahlung',
    },
    // How It Works Page - PHOTO ACCESS (SELFIE)
    howItWorks: {
      title: 'Finde deine Fotos sicher',
      subtitle: 'Lade ein Selfie hoch, damit unser System automatisch nur deine Fotos erkennt.',
      step1: {
        title: 'Wähle deine Surfschule',
        description: 'Wähle die Schule, bei der du deinen Kurs gemacht hast',
      },
      step2: {
        title: 'Wähle das Datum deiner Session',
        description: 'Wähle den Tag, an dem du gesurft hast',
      },
      step3: {
        title: 'Lade ein Selfie hoch',
        description: 'Unser System erkennt automatisch nur deine Fotos',
      },
      step4: {
        title: 'Sieh dir deine Fotos an',
        description: 'Sieh alle Fotos, auf denen du erscheinst',
      },
      step5: {
        title: 'Füge deine Erinnerungen hinzu',
        description: 'Wähle die Fotos aus, die du behalten möchtest',
      },
      step6: {
        title: 'Bezahle sicher',
        description: 'Zahle schnell und sicher',
      },
      step7: {
        title: 'Sofortiger Download',
        description: 'Erhalte deine Fotos sofort ohne Wasserzeichen',
      },
      smartSearch: {
        title: 'Intelligente Suche',
        description: 'Dieser Prozess garantiert deine Privatsphäre und verhindert, dass andere Personen auf Fotos zugreifen oder Fotos kaufen, die ihnen nicht gehören.',
      },
      cta: 'Finde deine Fotos',
    },
    // About Me Page - ABOUT ME
    aboutMe: {
      label: 'Über mich',
      title: 'Deine besten',
      titleHighlight: 'Momente im Wasser einfangen',
      intro: 'Ich bin <strong>Germán Abreu</strong>, Fotograf mit Spezialisierung auf Surf und Wassersport auf Lanzarote. Ich lebe in La Santa und arbeite hauptsächlich in Famara und an anderen Surfspots der Insel.',
      paragraph1: 'Nach Jahren, in denen ich Surfer und Surfschüler fotografiert habe, erkannte ich immer dasselbe Problem: Die eigenen Fotos unter Hunderten von Bildern zu finden war langsam und unpraktisch.',
      paragraph2: 'So entstand <strong className="brand-name">Surf Photo Lanzarote</strong> — eine einfache, schnelle und sichere Möglichkeit, damit jede Person nur ihre eigenen Fotos findet, ohne endlose Suche und unter Wahrung der Privatsphäre.',
      mission: 'Mein Ziel ist, dass du mit einer authentischen Erinnerung an dein Erlebnis im Wasser nach Hause gehst. Denn Wellen vergehen… Erinnerungen bleiben.',
      stats: {
        experience: '+8 Jahre',
        experienceLabel: 'Erfahrung',
        schools: '+7',
        schoolsLabel: 'Partnerschulen',
      },
    },
    // Pricing Page
    pricing: {
      title: 'Preise',
      subtitle: 'Wähle die Option, die am besten zu dir passt',
      plan1: {
        title: 'Deine schönste Erinnerung',
        description: 'Ein einzelnes Foto in hoher Qualität',
        details: [
          'Sofortiger Download',
          'Hohe Auflösung',
          'JPG-Format',
          'Für den Druck geeignet',
          'Ohne Wasserzeichen',
        ],
      },
      plan2: {
        title: 'Alle Erinnerungen deiner Session',
        description: 'Alle Fotos derselben Person am selben Tag.',
        details: [
          'Alle Fotos des Tages',
          'Sofortiger Download',
          'Hohe Auflösung',
          'JPG-Format',
          'Ohne Wasserzeichen',
        ],
      },
      plan3: {
        title: 'Zusätzlicher Tag',
        description: 'Füge einen weiteren Tag zu deinem Paket hinzu.',
        details: [
          'Alle Fotos des zusätzlichen Tages',
          'Sofortiger Download',
          'Hohe Auflösung',
          'Ohne Wasserzeichen',
        ],
      },
      plan4: {
        title: 'Zusätzliche Person',
        description: 'Füge die Fotos einer weiteren Person hinzu. Sie können vom selben Tag oder von einem anderen Tag sein.',
        details: [
          'Alle Fotos einer weiteren Person',
          'Sofortiger Download',
          'Hohe Auflösung',
          'Ohne Wasserzeichen',
        ],
      },
      includes: 'Enthält',
      note: 'Alle Preise beinhalten den sofortigen Download ohne Wasserzeichen',
    },
    // FAQ Page
    faqs: {
      title: 'Häufig gestellte Fragen',
      subtitle: 'Finde Antworten auf die häufigsten Fragen zu Surf Photo Lanzarote',
      contactText: 'Du findest nicht die Antwort, die du suchst?',
      contactLink: 'Kontaktiere uns',
      questions: [
        {
          q: 'Wie finde ich meine Fotos?',
          a: 'Wähle deine Surfschule, den Tag deiner Session und lade ein Selfie hoch. Die Website zeigt dir nur die Fotos, auf denen du an diesem Tag erscheinst.',
        },
        {
          q: 'Warum muss ich ein Selfie hochladen?',
          a: 'Wir nutzen Gesichtserkennung, um dein Gesicht zu identifizieren und dir nur deine Fotos anzuzeigen, ohne Hunderte von Bildern durchsuchen zu müssen.',
        },
        {
          q: 'Was passiert mit dem Selfie, das ich hochlade?',
          a: 'Das Selfie wird nur für die Suche an diesem Tag verwendet und weder gespeichert noch für andere Zwecke genutzt.',
        },
        {
          q: 'Kann ich mit einem Selfie Fotos von mehreren Tagen suchen?',
          a: 'Nein. Jede Suche erfolgt pro Tag. Wenn du an verschiedenen Tagen gesurft hast, musst du für jede Session ein Selfie hochladen, um deine Fotos zu finden.',
        },
        {
          q: 'Was tun, wenn wir zu zweit oder mehr sind?',
          a: 'Jede Person muss ihr eigenes Selfie hochladen, um ihre Fotos zu finden. Du kannst die Fotos jeder Person in den Warenkorb legen und problemlos fortfahren.',
        },
        {
          q: 'Warum haben die Fotos ein Wasserzeichen?',
          a: 'Die Fotos werden mit Wasserzeichen angezeigt, um die Arbeit des Fotografen zu schützen. Nach dem Kauf kannst du sie ohne Wasserzeichen herunterladen.',
        },
        {
          q: 'Welche Qualität haben die heruntergeladenen Fotos?',
          a: 'Die Fotos werden in hoher Qualität heruntergeladen, bereit für den persönlichen Gebrauch, Social Media und den Druck.',
        },
        {
          q: 'Wie lange sind meine Fotos verfügbar?',
          a: 'Die Fotos bleiben für begrenzte Zeit online. Wir empfehlen, sie so bald wie möglich nach dem Kauf herunterzuladen.',
        },
        {
          q: 'Kann ich Fotos von mehr als einem Tag kaufen?',
          a: 'Ja. Du kannst auf verschiedene Tage zugreifen und die Fotos jeder Session unabhängig voneinander kaufen.',
        },
        {
          q: 'Sind die Fotos öffentlich?',
          a: 'Nein. Die Fotos sind nach Schule und Tag organisiert, damit jeder Kunde nur auf seine eigene Session zugreifen kann.',
        },
        {
          q: 'Ist die Zahlung sicher?',
          a: 'Ja. Alle Zahlungen werden über Stripe abgewickelt, eine sichere und zuverlässige Zahlungsplattform.',
        },
        {
          q: 'Was tun, wenn ich mich nicht finde oder ein Problem habe?',
          a: 'Wenn du nicht auf den Fotos erscheinst oder ein Problem hast, kontaktiere uns und wir helfen dir.',
        },
      ],
    },
    // School Days Page
    schoolDays: {
      title: 'Wähle einen Tag',
      subtitle: 'Wähle den Tag deiner Surf-Session',
      noPhotos: 'Für diese Schule sind noch keine Fotos verfügbar.',
      back: 'Zurück',
      searchByDate: 'Nach Datum suchen',
      search: 'Suchen',
      availableDays: 'Verfügbare Tage',
      selectDaySubtitle: 'Wähle einen Tag, um deine Fotos zu finden',
      loadingDays: 'Verfügbare Tage werden geladen...',
      noDaysYet: 'Für diese Schule sind noch keine Tage verfügbar.',
      noDaysDesc: 'Fotos sind nach Tag organisiert. Schau bald wieder vorbei für neue Sessions.',
      photos: 'Fotos',
      errorLoadingDays: 'Fehler beim Laden der verfügbaren Tage',
      noPhotosForDate: 'Für dieses Datum sind keine Fotos verfügbar',
    },
    // Day Photos Page
    dayPhotos: {
      title: 'Deine Surffotos',
      subtitle: 'Fotos vom',
      findTitle: 'Finde deine Fotos',
      findSubtitle: 'Wähle, ob du ein Selfie hochlädst oder die Kamera einschaltest, damit wir dein Gesicht erkennen.',
      clickToSelect: 'Klicke, um ein Selfie auszuwählen',
      orDragImage: 'oder ziehe ein Bild hierher',
      faceGuide: {
        hintStarting: 'Gesichtsführung wird vorbereitet…',
        hintNoFace: 'Platziere dein Gesicht im Oval',
        hintMultiple: 'Bitte nur eine Person vor der Kamera',
        hintTooFar: 'Komm etwas näher',
        hintTooClose: 'Geh etwas weiter weg',
        hintMoveLeft: 'Beweg dich etwas nach links',
        hintMoveRight: 'Beweg dich etwas nach rechts',
        hintMoveUp: 'Hebe das Gesicht etwas an',
        hintMoveDown: 'Senke das Gesicht etwas',
        hintLookStraight: 'Schau gerade in die Kamera',
        turnLeft: 'Drehe den Kopf nach links und halte',
        turnRight: 'Jetzt den Kopf nach rechts drehen',
        circleHead: 'Kreise den Kopf langsam',
        coverage: 'Gesicht {{pct}} %',
        hintHoldStill: 'Perfekt, bitte stillhalten…',
        hintReady: 'Aufgenommen',
        cameraDenied: 'Kamerazugriff wurde verweigert. Du kannst ein Selfie hochladen oder es erneut versuchen.',
        choosePrompt: 'Wie sollen wir dein Gesicht finden?',
        uploadSelfie: 'Selfie hochladen',
        uploadSelfieHint: 'Nutze ein Foto, das du schon hast',
        startCamera: 'Kamera einschalten',
        startCameraHint: 'Wir führen dich, damit wir dein Gesicht erkennen',
        backToChoice: 'Andere Option wählen',
        capture: 'Aufnehmen',
        useGallery: 'Aus der Galerie hochladen',
        loadingDetector: 'Kamera wird gestartet…',
        galleryMultipleFaces: 'Lade ein Selfie mit nur einer Person hoch (kein Gruppenfoto).',
        galleryNoFace: 'Kein Gesicht erkannt. Bitte ein anderes Selfie versuchen.',
      },
      changePhoto: 'Foto ändern',
      analyzing: 'Analyse läuft...',
      searchPhotos: 'Meine Fotos finden',
      errorReadFile: 'Fehler beim Lesen der Datei. Bitte versuche ein anderes Bild.',
      errorSelectFirst: 'Bitte wähle zuerst ein Foto aus',
      errorEmptyFile: 'Die ausgewählte Datei ist leer. Bitte wähle ein anderes Bild.',
      errorAnalyze: 'Fehler bei der Fotoanalyse. Prüfe, ob der Server läuft.',
      yourSelfie: 'Dein Selfie',
      analyzingFace: 'Dein Gesicht wird analysiert...',
      streamingMatchesSoFar: '{{count}} Treffer bisher — Suche läuft weiter…',
      slowPathHint:
        'dieser Ordner ist noch nicht indexiert, daher vergleichen wir Foto für Foto (nur beim ersten Mal langsam).',
      autoIndexHint:
        'Index wird im Hintergrund erstellt — deine nächste Suche in diesem Ordner wird deutlich schneller sein.',
      indexClustersHint:
        'Gesichtsindex (Clusters) wird erstellt… das passiert einmal pro Schule/Tag — danach nutzen Suchen nur noch Clusters.',
      searchDiagnostics:
        'Gesichtssuche: {{path}} · Modus {{mode}} · {{seconds}}s',
      searchingIn: 'Suche nach Treffern in',
      analysisResults: 'Analyseergebnisse',
      matches: 'Treffer',
      photosAnalyzed: 'Analysierte Fotos',
      percentage: 'Prozent',
      photosFound: 'Gefundene Fotos',
      addAllMemories: 'Alle deine Erinnerungen hinzufügen',
      addAllMemoriesFor35: 'Alle deine Erinnerungen für {{price}}€ hinzufügen',
      similarity: 'Ähnlichkeit',
      buy: 'Kaufen',
      viewLarge: 'In voller Größe ansehen',
      noMatch: 'Kein Treffer',
      moreItems: 'weitere',
      uploadPhoto: 'Lade dein Foto hoch',
      uploadDescription: 'Lade ein klares Foto deines Gesichts hoch, um deine Surffotos zu finden',
      selectFile: 'Foto auswählen',
      findPhotos: 'Meine Fotos finden',
      results: 'Fotos gefunden',
      addToCart: 'In den Warenkorb',
      noResults: 'Keine Fotos gefunden. Versuche, ein anderes Foto hochzuladen.',
      savedSearchBanner: 'Deine gespeicherte Suche wird angezeigt — alle gefundenen Fotos aus deinem Konto.',
      newSearchLink: 'Neue Suche mit einem anderen Selfie starten',
      savedSearchLoading: 'Gespeicherte Suche wird geladen...',
      savedSearchNotFound: 'Gespeicherte Suche nicht gefunden. Du kannst unten eine neue Suche starten.',
      partnerOffer: 'Angebot',
      partnerOfferCta: 'Angebot ansehen',
      partnerOffersWhileSearching: 'Partnerangebote, während wir deine Fotos suchen',
    },
    // Checkout Success Page
    account: {
      loginTitle: 'Anmelden',
      registerTitle: 'Profil erstellen',
      loginLead: 'Greife bis zu 90 Tage lang auf deine Käufe und gespeicherten Suchen zu.',
      continueGoogle: 'Mit Google fortfahren',
      orEmail: 'oder mit E-Mail',
      displayName: 'Name',
      email: 'E-Mail',
      password: 'Passwort',
      passwordStrengthLabel: 'Passwortstärke',
      strengthWeak: 'Schwach',
      strengthFair: 'Mittel',
      strengthGood: 'Gut',
      strengthStrong: 'Stark',
      strengthHint: 'Verwende Großbuchstaben, Zahlen und Symbole',
      signIn: 'Anmelden',
      signUp: 'Konto erstellen',
      forgotPassword: 'Passwort vergessen?',
      forgotPasswordTitle: 'Passwort zurücksetzen',
      forgotPasswordLead: 'Gib deine E-Mail ein und wir senden dir einen Link, um ein neues Passwort zu wählen.',
      sendResetLink: 'Link senden',
      resetLinkSent: 'Falls ein Konto mit dieser E-Mail existiert, haben wir einen Reset-Link gesendet.',
      resetLinkError: 'Link konnte nicht gesendet werden. Versuche es erneut.',
      backToSignIn: 'Zurück zur Anmeldung',
      resetPasswordTitle: 'Neues Passwort wählen',
      resetPasswordLead: 'Gib ein neues Passwort für dein Konto ein.',
      newPassword: 'Neues Passwort',
      savePassword: 'Passwort speichern',
      passwordResetSuccess: 'Passwort aktualisiert. Du kannst dich jetzt anmelden.',
      passwordResetInvalid: 'Dieser Reset-Link ist abgelaufen oder ungültig.',
      passwordResetError: 'Passwort konnte nicht aktualisiert werden. Versuche es erneut.',
      passwordMismatch: 'Passwörter stimmen nicht überein.',
      confirmPassword: 'Passwort bestätigen',
      noAccount: 'Noch kein Konto?',
      haveAccount: 'Bereits ein Konto?',
      createAccount: 'Jetzt erstellen',
      photographerCta: 'Bist du Fotograf?',
      photographerRegister: 'Fotografen-Konto erstellen',
      photographerPanel: 'Fotografen-Bereich',
      verifyEmailSent: 'Prüfe deine E-Mail, um dein Konto zu verifizieren.',
      backHome: 'Zur Startseite',
      oauthLoading: 'Anmeldung läuft...',
      oauthError: 'Anmeldung fehlgeschlagen. Versuche es erneut.',
      oauthMissing: 'Ungültiger Anmeldelink.',
      emailVerifiedTitle: 'E-Mail verifiziert',
      emailVerifiedBody: 'Dein Profil ist aktiv. Du kannst dich anmelden und auf deine Galerie zugreifen.',
      goToAccount: 'Zu meinem Konto',
      emailInvalidTitle: 'Ungültiger Link',
      emailInvalidBody: 'Der Verifizierungslink ist abgelaufen oder ungültig.',
      checkEmailTitle: 'Prüfe deine E-Mail',
      checkEmailBody: 'Wir haben dir einen Link zur Kontoverifizierung gesendet.',
      checkEmailSpam: 'Falls du ihn nicht siehst, prüfe Spam oder Werbung.',
      resendVerification: 'Verifizierungs-E-Mail erneut senden',
      resendSuccess: 'Falls das Konto existiert, haben wir eine neue E-Mail gesendet.',
      resendError: 'E-Mail konnte nicht erneut gesendet werden. Versuche es erneut.',
      resendEmailRequired: 'Gib deine E-Mail ein, um erneut zu senden.',
      emailNotConfigured:
        'E-Mail konnte vom Server nicht gesendet werden. Kontaktiere den Support oder melde dich mit Google an.',
      registerCreatedNoEmailTitle: 'Konto erstellt',
      registerCreatedNoEmailBody:
        'Dein Konto wurde gespeichert, aber die Verifizierungs-E-Mail konnte noch nicht gesendet werden. Nutze unten „Erneut senden" oder melde dich mit Google an.',
      galleryTitle: 'Meine Galerie',
      profileSubtitle: 'Deine Käufe und gespeicherten Suchen, bis zu 90 Tage nach jeder Session.',
      tabProfile: 'Profil',
      hello: 'Hallo',
      signOut: 'Abmelden',
      tabPurchases: 'Käufe',
      tabSearches: 'Gespeicherte Suchen',
      statPurchases: 'Käufe',
      statSearches: 'Suchen',
      statPhotos: 'Fotos',
      editProfile: 'Profil bearbeiten',
      saveProfile: 'Änderungen speichern',
      profileSaved: 'Profil aktualisiert',
      profileSaveError: 'Profil konnte nicht gespeichert werden',
      changePhoto: 'Foto ändern',
      removePhoto: 'Foto entfernen',
      photoHint: 'JPG oder PNG, max. 5 MB. Wird als Profilbild verwendet.',
      photoUploadError: 'Foto konnte nicht hochgeladen werden',
      photoRemoved: 'Foto entfernt',
      photoUploaded: 'Foto aktualisiert',
      emailReadonly: 'E-Mail kann hier nicht geändert werden',
      authGoogle: 'Google-Konto',
      authEmail: 'E-Mail & Passwort',
      verified: 'Verifiziert',
      photosInOrder: 'Fotos',
      orderDate: 'Bestelldatum',
      downloadAvailable: 'Download verfügbar',
      loadError: 'Galerie konnte nicht geladen werden.',
      emailVerifyRequired:
        'Verifiziere deine E-Mail, um auf deine Galerie, Käufe und Downloads zuzugreifen.',
      verifyEmailLink: 'E-Mail verifizieren',
      noPurchases: 'Du hast noch keine Käufe.',
      noSearches: 'Noch keine gespeicherten Suchen.',
      order: 'Bestellung',
      downloadZip: 'ZIP herunterladen',
      expired: 'Download-Zeitraum abgelaufen',
      newSearch: 'Fotos von einem anderen Tag finden',
      photosFound: 'Fotos gefunden',
      viewSession: 'Session ansehen',
      searchSavedAt: 'Suche gespeichert',
      loading: 'Wird geladen...',
    },
    profilePrompt: {
      beforePayTitle: 'Profil erstellen',
      beforePayBody:
        'Melde dich mit Google oder E-Mail an, um deine Fotos jederzeit bis zu 90 Tage nach dem Session-Datum herunterzuladen.',
      afterPayTitle: 'Speichere deine Fotos in deinem Profil',
      afterPayBody:
        'Ohne Profil kannst du sie nur einmal jetzt herunterladen. Mit Profil hast du 90 Tage lang jederzeit Zugriff.',
      exitTitle: 'Ergebnisse speichern',
      exitBody: 'Erstelle ein Profil, damit du beim nächsten Mal kein Selfie erneut hochladen musst.',
      createProfile: 'Profil erstellen',
      saveToProfile: 'In meinem Profil speichern',
      saveResults: 'Ergebnisse speichern',
      continueGoogle: 'Mit Google fortfahren',
      notNow: 'Nicht jetzt',
      downloadNowOnly: 'Nur jetzt herunterladen',
    },
    checkoutSuccess: {
      loading: 'Downloads werden geladen...',
      paymentNotFound: 'Zahlung nicht gefunden',
      noDownloadData: 'Keine Download-Daten.',
      testHint: 'Um den Download-Bereich ohne Bezahlung zu testen, besuche:',
      backHome: 'Zur Startseite',
      title: 'Zahlung abgeschlossen!',
      subtitle: 'Danke für deinen Kauf. Hier sind deine Fotos zum Download ohne Wasserzeichen.',
      hello: 'Hallo',
      downloads: 'Downloads',
      downloadsIntro: 'Du kannst jedes Foto in hoher Qualität herunterladen, indem du auf den Button oder das Bild klickst.',
      downloadAll: 'Alle Fotos herunterladen',
      downloadingAll: 'Download wird gestartet…',
      downloadThis: 'Dieses Foto herunterladen',
      downloadingOne: 'Wird heruntergeladen…',
      downloadTitle: 'Herunterladen',
      total: 'Gesamt',
      photo: 'Foto',
      photos: 'Fotos',
      exploreMore: 'Weitere Schulen ansehen',
      missingSession: 'session_id fehlt oder Testmodus (test=1).',
      paymentIncomplete:
        'Wir konnten die Zahlungsdetails gerade nicht laden, aber wenn du die Zahlung abgeschlossen hast, war sie erfolgreich — bitte zahle nicht erneut. Aktualisiere diese Seite in Kürze oder kontaktiere uns mit deiner E-Mail und wir senden dir die Fotos.',
      loadError: 'Zahlungsdetails konnten nicht geladen werden.',
      unexpectedResponse: 'Unerwartete Serverantwort.',
      connectionError: 'Verbindungsfehler. Prüfe, ob das Backend läuft.',
      downloadsOnThisPage:
        'Deine Fotos sind auf dieser Seite. Nutze „Alle Fotos herunterladen" oder jeden Button „Dieses Foto herunterladen" unten.',
      guestProfileBenefit:
        'Du kannst deine Fotos hier jederzeit mit diesem Link herunterladen. Erstelle ein Profil, um sie in deiner Galerie zu speichern und jederzeit darauf zuzugreifen.',
      createProfileCta: 'Profil erstellen',
      goToGallery: 'Meine Galerie',
      downloadFailed: 'Download fehlgeschlagen. Versuche „Alle Fotos herunterladen" oder lade diese Seite neu.',
      voucherEmail:
        'Wir haben dir auch eine Kaufbestätigung mit Partner-Gutscheinen als PDF per E-Mail gesendet.',
    },
    checkoutCancel: {
      title: 'Zahlung abgebrochen',
      message: 'Es wurde nichts berechnet. Du kannst zum Warenkorb zurückkehren oder weiter stöbern.',
      backHome: 'Zur Startseite',
      viewSchools: 'Schulen ansehen',
    },
    // Cart Modal
    cart: {
      title: 'Deine Erinnerungen',
      item: 'Artikel',
      items: 'Artikel',
      empty: 'Dein Warenkorb ist leer',
      emptyDesc: 'Entdecke unsere Fotos und füge deine liebsten Erinnerungen hinzu',
      explorePhotos: 'Fotos entdecken',
      total: 'Gesamt',
      checkout: 'Zur Kasse',
      remove: 'Entfernen',
      beforePaymentDownloadNotice:
        'Deine Fotos stehen direkt nach der Zahlung auf dieser Seite zum Download bereit',
      emailPlaceholder: 'Deine E-Mail',
      namePlaceholder: 'Dein Name',
      subtotal: 'Zwischensumme',
      igic: 'IGIC ({rate}%)',
      taxesNotIncludedNote: 'Preise ohne Steuern',
      discount: 'Rabatt',
      processing: 'Wird verarbeitet...',
      proceedToPayment: 'Zur Zahlung',
      continueShopping: 'Weiter einkaufen',
      securePayment: 'Sichere Zahlung',
      instantDownload: 'Sofortiger Download',
      closeCart: 'Warenkorb schließen',
      removeItem: 'Artikel entfernen',
      packComplete: 'Volles Paket',
      photo: 'Foto',
      photos: 'Fotos',
      digitalFormat: 'Digitales Format',
      highResolution: 'Hohe Auflösung',
      errorEmailNameRequired: 'Gib deine E-Mail und deinen Namen ein, um fortzufahren',
      errorInvalidEmail: 'Gib eine gültige E-Mail ein (z. B. du@email.com)',
      errorEmptyCart: 'Dein Warenkorb ist leer',
      errorCheckoutSession: 'Zahlungssitzung konnte nicht erstellt werden',
      errorNoPaymentUrl: 'Zahlungs-URL nicht erhalten. Prüfe, ob Stripe konfiguriert ist.',
      errorPaymentFailed: 'Fehler bei der Zahlungsverarbeitung',
    },
    // Sticky purchase flow pricing bar
    purchasePricingBar: {
      ariaLabel: 'Preise',
      disclaimer:
        'Der Rabatt auf das 2. Paket gilt, wenn beide Pakete gemeinsam in den Warenkorb gelegt werden. Preise ohne Steuern (IGIC).',
      blocks: [
        {
          description: 'Dein bestes Foto',
          badge: '1 Foto',
        },
        {
          description: 'Alle deine Fotos einer Session',
          badge: 'Volles Paket',
        },
        {
          description: 'Extra-Session für dich',
          badge: '2. Paket',
        },
        {
          description: 'Alle Fotos eines Freundes oder Familienmitglieds',
          badge: '2. Paket',
        },
      ],
    },
    // Partners
    partners: {
      title: 'Partnerschulen',
    },
    // Footer
    footer: {
      brand: 'Surf Photo Lanzarote',
      description: 'Premium-Marktplatz für Surf-Fotografie auf Lanzarote',
      navigation: 'Navigation',
      home: 'Startseite',
      schools: 'Schulen',
      howItWorks: 'So funktioniert\'s',
      pricing: 'Preise',
      legal: 'Rechtliches',
      legalNotice: 'Impressum',
      privacy: 'Datenschutzerklärung',
      terms: 'AGB',
      contact: 'Kontakt',
      email: 'info@surfphotolanzarote.com',
      location: 'Lanzarote, Kanarische Inseln',
      rights: 'Alle Rechte vorbehalten',
    },
    // Common
    common: {
      loading: 'Wird geladen...',
      error: 'Ein Fehler ist aufgetreten',
      close: 'Schließen',
      cancel: 'Abbrechen',
      confirm: 'Bestätigen',
      save: 'Speichern',
      delete: 'Löschen',
      edit: 'Bearbeiten',
      back: 'Zurück',
    },
  };
